<template>
  <div class="w-full" data-testid="pinning-session-entry">
    <!-- Completed session summary -->
    <div
      v-if="artwork.pinningSessionId"
      :class="[
        'flex flex-col gap-3 rounded-lg p-2 md:p-3',
        artwork.pinningSessionState === 'approved' ? 'bg-[#AD47FF]/20' : 'bg-[#E1E4FD]'
      ]"
      data-testid="completed-session-indicator"
    >
      <div v-if="artwork.pinningSessionState === 'approved'" class="flex items-center gap-2">
        <UseSvg icon="sparkles-gradient" class="h-8 w-8 shrink-0" />
        <div>
          <p class="text-[15px] font-medium leading-6 text-[#0a0a0a]">AI Edit Applied</p>
          <p class="text-[11px] font-medium leading-[18px] text-gray-900">
            Your design has been updated by AI.
          </p>
        </div>
      </div>
      <div v-else class="flex items-center gap-2">
        <UseSvg icon="sparkles-gradient" class="h-8 w-8 shrink-0" />
        <div class="flex-1">
          <div class="flex justify-between gap-1.5">
            <div class="flex items-center gap-1.5">
              <p class="text-[15px] font-medium leading-6 text-[#0a0a0a]">Your Artist Edits</p>
              <span
                class="rounded-full border border-white bg-[#00834f] px-1.5 py-0.5 text-xs font-medium text-white"
              >
                Free
              </span>
            </div>
            <button
              ref="completedHowItWorksBtn"
              type="button"
              class="cursor-pointer border-0 bg-transparent p-0 text-xs !text-blue-700 underline"
              data-testid="how-it-works-button"
              @click="openHowItWorksModal"
              @mouseenter="isDesktop && showDesktopPopover()"
              @mouseleave="isDesktop && hideDesktopPopover()"
            >
              How it works
            </button>
          </div>
          <p class="text-[11px] font-medium leading-[18px] text-gray-900">
            Human artists will handle your edit after checkout.
          </p>
        </div>
      </div>
      <div
        v-if="artwork.pinningSummary"
        class="rounded-lg bg-white/70 p-3 text-xs font-medium text-gray-900"
        data-testid="markdown-content"
      >
        <div
          class="markdown-rendered text-inherit"
          v-html="renderMarkdown(artwork.pinningSummary)"
        />
      </div>
      <div class="flex gap-2 self-end">
        <button
          type="button"
          class="shrink-0 cursor-pointer rounded-[8px] border-[1.5px] border-[#1e2939] bg-white px-3.5 py-2 text-sm font-medium text-gray-900 [font-family:inherit] hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-25"
          :disabled="isCancelling"
          data-testid="cancel-design-request-button"
          @click="showConfirmModal = true"
        >
          {{ artwork.pinningSessionState === "approved" ? "Undo AI Edit" : "Cancel Request" }}
        </button>
      </div>
    </div>

    <!-- New session input -->
    <div v-else :class="{ 'cursor-not-allowed opacity-60': isEnhancementInProgress }">
      <div class="rounded-t-lg bg-[#AD47FF]/20 px-2 pb-4 pt-[3px] md:px-3">
        <ArtistEditsHeader
          ref="inputHeader"
          title="Edit with AI"
          @how-it-works-click="openHowItWorksModal"
          @how-it-works-mouseenter="isDesktop && showDesktopPopover()"
          @how-it-works-mouseleave="isDesktop && hideDesktopPopover()"
        />
      </div>
      <div
        class="gradient-border-purple-blue relative z-10 -mt-3 flex cursor-text flex-col gap-2 rounded-lg border-[1.5px] border-transparent px-2 py-1.5 md:px-3 md:py-2"
        @click="handleContainerClick"
      >
        <textarea
          ref="textareaRef"
          v-model="inputMessage"
          data-testid="request-designer-edit-input"
          class="mp-unmask border-0! outline-none! min-h-0 resize-none p-0 text-base placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
          placeholder="Tell us what you'd like changed."
          rows="2"
          :disabled="isLoading || isEnhancementInProgress"
          @input="debouncedTrackEntryInput"
          @keydown="handleKeyDown"
        />
        <div class="flex cursor-pointer justify-end">
          <button
            type="button"
            data-testid="request-designer-edit-submit"
            :class="[
              'bg-gradient-purple-blue h-8 min-w-12 cursor-pointer rounded-lg border-0 px-2 !text-xs !font-semibold leading-4 !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25',
              !inputMessage.trim() && !isLoading && 'opacity-50'
            ]"
            :disabled="isLoading || isEnhancementInProgress"
            @click="handleSubmitMessage"
          >
            {{ isLoading ? "Launching\u2026" : "Launch" }}
          </button>
        </div>
      </div>
    </div>
    <!-- Desktop: hover popover -->
    <teleport to="body">
      <div
        v-if="isDesktop && isDesktopPopoverVisible"
        ref="desktopPopoverEl"
        class="fixed z-[10000] max-h-[90vh] w-[580px] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 text-sm shadow-lg"
        :style="desktopPopoverStyle"
        @mouseenter="showDesktopPopover()"
        @mouseleave="hideDesktopPopover()"
      >
        <ArtistEditsInformation />
      </div>
    </teleport>
    <!-- Mobile: fixed overlay (matches React PrintPerfectExplanatoryTooltip) -->
    <teleport to="body">
      <div
        v-if="!isDesktop && isHowItWorksOpen"
        class="fixed inset-0 z-[10000] flex items-center justify-center"
      >
        <div
          class="absolute inset-0 bg-black/60"
          data-testid="how-it-works-backdrop"
          @click="closeHowItWorks"
        />
        <div
          class="relative max-h-[95dvh] w-full max-w-[360px] overflow-y-auto rounded-lg bg-white px-[15px] py-[15px] pb-[max(15px,env(safe-area-inset-bottom))]"
        >
          <ArtistEditsInformation is-mobile @close="closeHowItWorks" />
        </div>
      </div>
    </teleport>
    <!-- Cancel confirmation modal -->
    <div
      v-if="showConfirmModal"
      class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
      data-testid="cancel-confirm-modal"
      @click.self="showConfirmModal = false"
    >
      <div class="w-full max-w-sm rounded-lg bg-white p-0 shadow-xl" @click.stop>
        <div class="border-b border-gray-200 px-6 py-4">
          <h3 class="m-0 text-base font-semibold text-gray-700">
            {{ isApprovedSession ? "Undo AI Edit?" : "Cancel Artist Request?" }}
          </h3>
        </div>
        <div class="px-6 pb-6 pt-4 text-center">
          <p class="m-0 mb-4 text-base font-medium text-gray-700">
            {{
              isApprovedSession
                ? "This will revert your design to the un-edited image. This can\u2019t be undone."
                : "Your edit request will be removed and won\u2019t be sent to our artists. You can always request it again."
            }}
          </p>
          <div class="flex flex-col gap-2">
            <button
              type="button"
              data-testid="cancel-confirm-button"
              class="w-full cursor-pointer rounded-[8px] border border-red-600 bg-white px-3.5 py-2 text-base font-semibold [font-family:inherit]"
              style="color: #dc2626"
              @click="confirmCancel"
            >
              {{ isApprovedSession ? "Yes, revert to original" : "Yes, cancel request" }}
            </button>
            <button
              type="button"
              data-testid="cancel-keep-button"
              class="w-full cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-2 text-base font-semibold !text-white [font-family:inherit]"
              @click="showConfirmModal = false"
            >
              {{ isApprovedSession ? "Keep AI edit" : "Keep request" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { isMediumDeviceAndUp } from "common/utils/browser";
import UseSvg from "common/vue/components/UseSvg.vue";
import debounce from "lodash.debounce";
import { computed, ref } from "vue";
import { useDtfStore } from "../store";
import ArtistEditsHeader from "./ArtistEditsHeader.vue";
import ArtistEditsInformation from "./ArtistEditsInformation.vue";
import { useMarkdownRenderer } from "./composables/useMarkdownRenderer";
import { trackTransfersPinningSessionEntryInput } from "./transfersPinningAnalytics";

defineOptions({ name: "PinningSessionEntry" });

const { renderMarkdown } = useMarkdownRenderer();

const props = defineProps({
  index: { type: Number, required: true },
  artwork: { type: Object, required: true }
});

const store = useDtfStore();
const isLoading = computed(() => store.pinningSessionLoading);
const isEnhancementInProgress = computed(() => {
  if (!props.artwork?.id || props.artwork.isSimulated) return true;
  if (props.artwork.isLoading) return true;
  return store.isStandardArtwork && !props.artwork.enhancementCompleted;
});
const isDesktop = isMediumDeviceAndUp();
const showConfirmModal = ref(false);
const isApprovedSession = computed(() => props.artwork.pinningSessionState === "approved");
const textareaRef = ref(null);
const inputMessage = ref("");
const isCancelling = ref(false);
const isHowItWorksOpen = ref(false);
const completedHowItWorksBtn = ref(null);
const inputHeader = ref(null);
const isDesktopPopoverVisible = ref(false);
const desktopPopoverStyle = ref({});
const desktopPopoverEl = ref(null);
let popoverHideTimeout = null;

function $getHowItWorksButton() {
  return completedHowItWorksBtn.value ?? inputHeader.value?.howItWorksButtonRef ?? null;
}

function showDesktopPopover() {
  clearTimeout(popoverHideTimeout);
  const $button = $getHowItWorksButton();
  if ($button) {
    const rect = $button.getBoundingClientRect();
    const popoverWidth = 580;
    const gap = 8;
    const margin = 8;

    // Prefer left side; flip to right if not enough space
    let left = rect.left - popoverWidth - gap;
    if (left < margin) {
      left = rect.right + gap;
    }
    // Clamp to viewport
    left = Math.max(margin, Math.min(left, window.innerWidth - popoverWidth - margin));

    // First render to get popover height, then clamp vertically
    desktopPopoverStyle.value = { left: `${left}px`, top: "0px", visibility: "hidden" };
    isDesktopPopoverVisible.value = true;

    requestAnimationFrame(() => {
      const el = desktopPopoverEl.value;
      const popoverHeight = el ? el.offsetHeight : 400;
      const buttonCenterY = rect.top + rect.height / 2;
      const idealTop = buttonCenterY - popoverHeight / 2;
      const clampedTop = Math.max(
        margin,
        Math.min(idealTop, window.innerHeight - popoverHeight - margin)
      );

      desktopPopoverStyle.value = { left: `${left}px`, top: `${clampedTop}px` };
    });
    return;
  }
  isDesktopPopoverVisible.value = true;
}

function hideDesktopPopover() {
  popoverHideTimeout = setTimeout(() => {
    isDesktopPopoverVisible.value = false;
  }, 150);
}

function openHowItWorksModal() {
  if (!isDesktop) {
    isHowItWorksOpen.value = true;
  }
}

function closeHowItWorks() {
  isHowItWorksOpen.value = false;
}

function handleContainerClick(e) {
  if (e.target.closest("button, input, label, a")) {
    return;
  }

  const buttons = Array.from(e.currentTarget.querySelectorAll("button, input, label, a"));
  for (const el of buttons) {
    const rect = el.getBoundingClientRect();
    if (
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom
    ) {
      return;
    }
  }

  textareaRef.value?.focus();
}

const debouncedTrackEntryInput = debounce(() => {
  if (props.artwork?.id) {
    trackTransfersPinningSessionEntryInput({
      artworkId: props.artwork.id,
      inputValue: inputMessage.value
    });
  }
}, 500);

async function handleSubmitMessage() {
  const trimmed = inputMessage.value.trim();
  if (!trimmed || isLoading.value || isEnhancementInProgress.value) return;
  await store.openPinningSession(props.index, trimmed);
  if (store.showPinningModal) {
    inputMessage.value = "";
  }
}

function handleKeyDown(e) {
  if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
    e.preventDefault();
    handleSubmitMessage();
  }
}

function handleRemove() {
  isCancelling.value = true;
  store.clearPinningSession(props.index);
}

function confirmCancel() {
  handleRemove();
  showConfirmModal.value = false;
}
</script>

<style scoped>
.markdown-rendered :deep(ul) {
  margin-left: 1rem;
  list-style-type: disc;
}

.markdown-rendered :deep(ul li) {
  list-style-type: disc;
}

.markdown-rendered :deep(ul),
.markdown-rendered :deep(ol) {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.markdown-rendered :deep(ol) {
  margin-left: 1rem;
  list-style-type: decimal;
}

.markdown-rendered :deep(ol li) {
  list-style-type: decimal;
}

.markdown-rendered :deep(p) {
  margin-bottom: 0.5rem;
}

.markdown-rendered :deep(p:last-child) {
  margin-bottom: 0;
}

.markdown-rendered :deep(strong) {
  font-weight: 600;
}

.markdown-rendered :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}

.markdown-rendered :deep(a:hover) {
  color: #1e40af;
}

.markdown-rendered :deep(blockquote) {
  border-left: 4px solid #d1d5db;
  padding-left: 0.75rem;
  font-style: italic;
  color: #4b5563;
}

.markdown-rendered :deep(code) {
  border-radius: 0.25rem;
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  font-family: ui-monospace, monospace;
  font-size: 0.875rem;
}

.markdown-rendered :deep(pre) {
  overflow-x: auto;
  border-radius: 0.375rem;
  background-color: #f3f4f6;
  padding: 0.5rem;
  font-size: 0.875rem;
}
</style>
